import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, UserCheck, CalendarClock, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const Tile = ({ icon: Icon, label, value, to, testid }) => (
  <Link
    to={to}
    className="card-soft group block p-5 transition-transform duration-150 hover:-translate-y-0.5"
    data-testid={testid}
  >
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        <p className="metric mt-2 text-3xl font-bold text-foreground">{value}</p>
      </div>
      <Icon className="h-5 w-5 text-primary" />
    </div>
    <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-primary">
      Open
      <ArrowRight
        className="h-3.5 w-3.5 transition-transform duration-150 group-hover:translate-x-1"
      />
    </div>
  </Link>
);

export default function HrDashboard() {
  const [attendance, setAttendance] = useState(null);
  const [employees, setEmployees] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/hrms/attendance/today"),
      api.get("/hrms/employees", { params: { page_size: 1, status: "active" } }),
    ])
      .then(([attendanceRes, employeesRes]) => {
        setAttendance(attendanceRes.data);
        setEmployees(employeesRes.data);
      })
      .catch(() => {
        setError("HR data is available only to authorised HR and workforce managers.");
      });
  }, []);

  const counts = attendance?.counts || {};
  return (
    <div className="space-y-6" data-testid="hrms-dashboard-page">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-primary">
          People operations
        </p>
        <h1 className="mt-1 text-4xl font-bold">HR & Workforce Management</h1>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          Employee records, approved shifts, leave workflows, and immutable attendance events.
        </p>
      </div>
      {error && (
        <p className="text-sm text-destructive" data-testid="hrms-dashboard-error">
          {error}
        </p>
      )}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Tile
          icon={Users}
          label="Active employees"
          value={employees?.total ?? "—"}
          to="/hrms/employees"
          testid="hrms-active-employees"
        />
        <Tile
          icon={UserCheck}
          label="Present today"
          value={counts.present ?? 0}
          to="/hrms/attendance"
          testid="hrms-present-today"
        />
        <Tile
          icon={CalendarClock}
          label="Missed punches"
          value={counts.missed_punch ?? 0}
          to="/hrms/attendance"
          testid="hrms-missed-punches"
        />
        <Tile
          icon={CalendarClock}
          label="Half days"
          value={counts.half_day ?? 0}
          to="/hrms/attendance"
          testid="hrms-half-days"
        />
      </div>
      <section
        className="border-l-4 border-primary bg-primary/5 px-5 py-4"
        data-testid="hrms-privacy-notice"
      >
        <p className="text-sm font-semibold">Attendance privacy control</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Face liveness and biometric attendance remain disabled pending an approved provider.
          Employees can use approved alternatives and attendance corrections with audit history.
        </p>
      </section>
    </div>
  );
}